import * as React from 'react';
import { applyRunwayTheme } from './globalStyles';
import useIsRunwayAdmin from '@/utils/hooks/useIsRunwayAdmin';

export type ThemeMode = 'light' | 'dark';

const STORAGE_KEY = 'runway.themeMode';

function getInitialThemeMode(): ThemeMode {
    try {
        const stored = window.localStorage.getItem(STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    } catch {
        // ignore (storage may be unavailable)
    }

    if (typeof window !== 'undefined' && window.matchMedia) {
        return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }

    return 'light';
}

type ThemeModeContextValue = {
    mode: ThemeMode;
    setMode: (mode: ThemeMode) => void;
    toggleMode: () => void;
};

const defaultContextValue: ThemeModeContextValue = {
    mode: 'light',
    setMode: () => {},
    toggleMode: () => {},
};

const ThemeModeContext = React.createContext<ThemeModeContextValue>(defaultContextValue);

export const ThemeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const isRunwayAdmin = useIsRunwayAdmin();
    const [mode, setMode] = React.useState<ThemeMode>(() => getInitialThemeMode());

    React.useEffect(() => {
        // Only apply theme changes for runway admins
        if (!isRunwayAdmin) {
            return;
        }
        document.documentElement.setAttribute('data-theme', mode);
        applyRunwayTheme(mode);

        try {
            window.localStorage.setItem(STORAGE_KEY, mode);
        } catch {
            // ignore
        }
    }, [mode, isRunwayAdmin]);

    const toggleMode = React.useCallback(() => {
        setMode((prev) => (prev === 'dark' ? 'light' : 'dark'));
    }, []);

    // For non-admins, just render children with default (light mode) context
    if (!isRunwayAdmin) {
        return <ThemeModeContext.Provider value={defaultContextValue}>{children}</ThemeModeContext.Provider>;
    }

    return <ThemeModeContext.Provider value={{ mode, setMode, toggleMode }}>{children}</ThemeModeContext.Provider>;
};

export function useThemeMode(): ThemeModeContextValue {
    return React.useContext(ThemeModeContext);
}
